<template>
  <h4>{{ $t("strings.inputs-values") }}</h4>
  <h6>{{ $t("strings.drag-inputs-tip") }}</h6>
  <v-sheet
    id="inputListTree"
    class="overflow-y-auto pt-4"
    :max-height="data.parentContentHeight"
  >
    <v-list
      id="treelist"
      class="text-caption"
      density="compact"
      nav
      :slim="true"
    >
      <template v-for="(value, key, idx) in inputsData">
        <v-list-group
          v-if="isObject(value)"
          :key="idx"
          :draggable="true"
          @dragstart="onDrag($event, key, value)"
        >
          <template v-slot:activator="{ props }">
            <v-list-item v-bind="props" class="text-caption">
              <v-list-item-title
                class="d-flex flex-row flex-nowrap align-center"
                >{{ nodeTitle(key) }}
                <v-btn
                  v-if="key.startsWith('op_')"
                  @click.stop="openPreview(true, key, key)"
                  icon="mdi-play-circle"
                  variant="text"
                  color="secondary"
                  density="compact"
                  class="ml-2"
                ></v-btn
              ></v-list-item-title>

              <template v-slot:prepend>
                <v-icon
                  :icon="
                    Array.isArray(value) ? 'mdi-code-brackets' : 'mdi-code-json'
                  "
                  :color="
                    Array.isArray(value)
                      ? 'light-blue-darken-2'
                      : 'blue-grey-darken-4'
                  "
                ></v-icon>
              </template>
            </v-list-item>
          </template>
          <RecursiveListTree :jsonObj="value" :path="key" @onDrag="onDrag" />
        </v-list-group>
        <v-list-item
          v-else
          :key="`item-${idx}`"
          class="text-caption"
          :draggable="true"
          @dragstart="onDrag($event, key, value)"
        >
          <v-list-item-title class="d-flex flex-row flex-nowrap align-center"
            >{{ nodeTitle(key) }}
            <v-btn
              v-if="key.startsWith('op_')"
              @click.stop="openPreview(true, key, key)"
              icon="mdi-play-circle"
              variant="text"
              color="secondary"
              density="compact"
              class="ml-2"
            ></v-btn
          ></v-list-item-title>
          <v-list-item-subtitle>{{ value }}</v-list-item-subtitle>

          <template v-slot:prepend>
            <v-icon icon="mdi-location-enter" color="green-darken-2"></v-icon>
          </template>
        </v-list-item>
      </template>
    </v-list>
  </v-sheet>
  <DialogPreview
    v-if="showDialogPreview"
    :custom="true"
    :showDialogPreview="showDialogPreview"
    :targetLabel="previewTargetLabel"
    :targetId="previewTargetId"
    :nodes="findNodesByType(nodes, 'inputNode')"
    :apiDraw="apiDraw"
    @toggleDialogPreview="toggleDialogPreview"
    @updatePreview="updatePreview"
  ></DialogPreview>
</template>

<script>
import { computed, inject, ref } from "vue";
import RecursiveListTree from "./RecursiveListTree.vue";
import { drag, recognizeType } from "@/mixins/common-utils.js";
import jsonpath from "jsonpath";
import { useI18n } from "vue-i18n";
import DialogPreview from "../DialogPreview.vue";
import { findNodesByType } from "../../mixins/VueFlowEditor.js";
import { useVueFlow } from "@vue-flow/core";

export default {
  props: ["inputs", "data"],
  components: {
    RecursiveListTree,
    DialogPreview,
  },
  emits: ["setSnackbar"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const findNodeById = inject("findNodeById");
    let showDialogPreview = ref(false);
    let previewTargetLabel = ref(null);
    let previewTargetId = ref(null);
    const { nodes } = useVueFlow();
    const config = inject("config");
    let apiDraw = computed(() => {
      return config.apiDraw;
    });

    function openPreview(value, targetId, targetLabel) {
      previewTargetLabel.value = targetLabel;
      previewTargetId.value = targetId;
      toggleDialogPreview(value);
    }
    function toggleDialogPreview(value) {
      showDialogPreview.value = value;
    }
    function updatePreview(id, value) {
      updatePreviewById(id, value);
    }
    function updatePreviewById(id, newPreview) {
      // Find the object with the specified ID
      const obj = props.inputs.find((item) => item.id === id);
      // If the object exists and has a "preview" key, replace the value
      if (obj && "preview" in obj) {
        obj.preview = newPreview;
      }
    }

    function nodeTitle(key) {
      const node = findNodeById(key);
      return node.userData.label
        ? node.userData.label + " (" + key + ")"
        : t("labels.preview") + " (" + node.id + ")";
    }

    //Transforms the array props.inputs into an object containing the values of the inputs to the Operator
    let inputsData = computed(() => {
      let json = props.inputs?.reduce((accumulator, obj) => {
        if (obj) {
          let key = obj?.id;
          let value = obj?.preview || obj?.userData?.defaultValue || "";
          value =
            obj?.userData?.contentType === "json"
              ? handleInput(value, obj?.userData?.label)
              : value;
          accumulator[key] = value;
        }
        return accumulator;
      }, {});
      return json;
    });

    //Checks the type of input and returns the value accordingly
    function handleInput(input, input_name) {
      if (typeof input === "string") {
        try {
          return JSON.parse(input);
        } catch {
          emit(
            "setSnackbar",
            true,
            t(`strings.input-node-invalid`, {
              input_name: input_name,
            }),
            "warning",
            -1,
          );
          return input;
        }
      } else if (input !== null && typeof input === "object") {
        return input;
      } else {
        throw new Error("The input type is neither a string nor an object.");
      }
    }

    //Obtain the json-path for the key from the json object
    function findJsonPathForKey(jsonObj, key) {
      let result = jsonpath.query(jsonObj, `$..${key}`);
      if (result.length > 0) {
        return jsonpath.nodes(jsonObj, `$..${key}`)[0].path;
      } else {
        console.error("No matching keys found for key: ", key);
        return null;
      }
    }
    //Format value to the BE in {id_json:$...} for json input or {id} for others type
    function rewriteString(jpathNodes, valueType) {
      if (valueType != "text" || jpathNodes.length > 2) {
        let newStr = jpathNodes[1].startsWith("in_")
          ? `{${jpathNodes[1]}_json:$`
          : `{${jpathNodes[1]}:$`;
        for (let i = 2; i < jpathNodes.length; i++) {
          newStr += `.${jpathNodes[i]}`;
        }
        newStr += "}";
        return newStr;
      } else {
        return `{${jpathNodes[1]}}`;
      }
    }
    //Manages dragged item before to call drag method
    function onDrag(event, key, value) {
      const jpathValue = findJsonPathForKey(inputsData.value, key);
      const formattedValue = rewriteString(jpathValue, recognizeType(value));
      const arrayValues = JSON.stringify([formattedValue, value]);
      drag(event, arrayValues);
    }
    //Checks if it is an object
    function isObject(obj) {
      return (
        JSON.parse(JSON.stringify(obj)) !== null &&
        (typeof obj === "object" || Array.isArray(obj))
      );
    }

    return {
      drag,
      inputsData,
      onDrag,
      isObject,
      findNodeById,
      nodeTitle,
      openPreview,
      findNodesByType,
      nodes,
      showDialogPreview,
      previewTargetLabel,
      previewTargetId,
      toggleDialogPreview,
      apiDraw,
      updatePreview,
    };
  },
};
</script>
<style scoped>
#treelist :deep(.v-list-item__prepend .v-icon) {
  margin-inline-end: 0px;
}
</style>
